/* eslint-disable */
// src/layouts/visitMachine/strategies/TragamonedaStrategy.js

import { calculateFormula } from "./calculateFormula";
import { resolveProfitPercentage } from "./resolveProfitPercentage";
import { getMachineFormula } from "utils/machineFormula";

export class TragamonedaStrategy {
  // picker robusto: busca el primer número válido entre varias claves
  pickNum(obj, keys = [], fallback = 0) {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null && !Number.isNaN(Number(v))) {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
    }
    return fallback;
  }

  calculate(machine, userInputs, visitMachineDetails, profitPercentages, isEditMode, prevRecords) {
    // en tu UI los inputs se guardan por LocalMachineId
    const keyId = Number(machine?.LocalMachineId ?? machine?.Id);
    const inputs = userInputs?.[keyId] || {};

    const isAccumulative = Boolean(machine.IsAccumulative);
    const hasReset = Boolean(inputs?.HasReset !== undefined ? inputs.HasReset : machine?.HasReset);
    const postResetStartIn = hasReset
      ? this.pickNum(inputs, ["PostResetStartIn"], this.pickNum(machine, ["PostResetStartIn"], 0))
      : null;
    const postResetStartOut = hasReset
      ? this.pickNum(inputs, ["PostResetStartOut"], this.pickNum(machine, ["PostResetStartOut"], 0))
      : null;

    // original también debe matchear por LocalMachineId (no por machine.Id)
    const original = isEditMode
      ? (prevRecords || []).find((r) => Number(r.LocalMachineId) === keyId)
      : null;

    // lastVisit debe buscar por LocalMachineId (keyId)
    const lastVisit = (visitMachineDetails || []).find((v) => Number(v.LocalMachineId) === keyId) || {};
    const lastHadReset = Boolean(lastVisit?.HasReset ?? lastVisit?.hasReset);
    const lastPostResetIn = this.pickNum(lastVisit, ["PostResetStartIn", "postResetStartIn"], null);
    const lastPostResetOut = this.pickNum(lastVisit, ["PostResetStartOut", "postResetStartOut"], null);

    // En edición, el snapshot persistido es la fuente autoritativa del previo histórico.
    // El previo refrescado por fecha queda como fallback porque una recreación append-only
    // de LocalMachine puede impedir que esa consulta encuentre la identidad histórica.
    const historicalInCredits = isEditMode
      ? this.pickNum(original, ["PrevCreditsIn", "prevCreditsIn", "LastCreditsIn", "lastCreditsIn"], null)
      : null;
    const historicalOutCredits = isEditMode
      ? this.pickNum(original, ["PrevCreditsOut", "prevCreditsOut", "LastCreditsOut", "lastCreditsOut"], null)
      : null;
    const refreshedInCredits = this.pickNum(
      lastVisit,
      ["PrevCreditsIn", "CreditsIn", "lastCreditsIn"],
      null
    );
    const refreshedOutCredits = this.pickNum(
      lastVisit,
      ["PrevCreditsOut", "CreditsOut", "lastCreditsOut"],
      null
    );
    const lastInCredits = historicalInCredits ?? refreshedInCredits;
    const lastOutCredits = historicalOutCredits ?? refreshedOutCredits;

    const historicalInMoney = isEditMode
      ? this.pickNum(original, ["PrevMoneyIn", "prevMoneyIn", "LastMoneyIn", "lastMoneyIn"], null)
      : null;
    const historicalOutMoney = isEditMode
      ? this.pickNum(original, ["PrevMoneyOut", "prevMoneyOut", "LastMoneyOut", "lastMoneyOut"], null)
      : null;
    const refreshedInMoney = this.pickNum(
      lastVisit,
      ["PrevMoneyIn", "MoneyIn", "lastMoneyIn"],
      0
    );
    const refreshedOutMoney = this.pickNum(
      lastVisit,
      ["PrevMoneyOut", "MoneyOut", "lastMoneyOut"],
      0
    );
    const lastInMoney = historicalInMoney ?? refreshedInMoney;
    const lastOutMoney = historicalOutMoney ?? refreshedOutMoney;

    const formula = getMachineFormula(machine);
    const hasFormula = Boolean(formula);

    // === MODO "SIN FÓRMULA" =====
    if (!hasFormula) {
      let actualMoneyIn = null;
      let actualMoneyOut = null;
      let netMoneyIn = null;
      let netMoneyOut = null;

      if (isAccumulative) {
        // Mantener comportamiento actual: usuario ingresa acumulados.
        const hasMoneyIn =
          inputs?.MoneyIn !== undefined && inputs?.MoneyIn !== null && inputs?.MoneyIn !== "";
        const hasMoneyOut =
          inputs?.MoneyOut !== undefined && inputs?.MoneyOut !== null && inputs?.MoneyOut !== "";

        actualMoneyIn = hasMoneyIn ? Number(inputs.MoneyIn) : null;
        actualMoneyOut = hasMoneyOut ? Number(inputs.MoneyOut) : null;

        netMoneyIn = actualMoneyIn != null ? actualMoneyIn - lastInMoney : null;
        netMoneyOut = actualMoneyOut != null ? actualMoneyOut - lastOutMoney : null;
      } else {
        // Regla nueva: usuario ingresa NETOS y persistimos acumulados calculados.
        const hasNetMoneyIn =
          inputs?.NetMoneyIn !== undefined && inputs?.NetMoneyIn !== null && inputs?.NetMoneyIn !== "";
        const hasNetMoneyOut =
          inputs?.NetMoneyOut !== undefined && inputs?.NetMoneyOut !== null && inputs?.NetMoneyOut !== "";

        netMoneyIn = hasNetMoneyIn ? Number(inputs.NetMoneyIn) : null;
        netMoneyOut = hasNetMoneyOut ? Number(inputs.NetMoneyOut) : null;

        const baseMoneyIn = (lastHadReset && lastPostResetIn != null) ? lastPostResetIn : lastInMoney;
        const baseMoneyOut = (lastHadReset && lastPostResetOut != null) ? lastPostResetOut : lastOutMoney;

        actualMoneyIn = netMoneyIn != null ? baseMoneyIn + netMoneyIn : null;
        actualMoneyOut = netMoneyOut != null ? baseMoneyOut + netMoneyOut : null;
      }

      const moneyCollected =
        netMoneyIn != null && netMoneyOut != null ? netMoneyIn - netMoneyOut : null;

      const profit = resolveProfitPercentage(machine, profitPercentages, isEditMode);

      const clientAmount =
        moneyCollected != null ? (moneyCollected * (profit.ClientPercentage || 0)) / 100 : null;

      const ownerAmount =
        moneyCollected != null ? (moneyCollected * (profit.OwnerPercentage || 0)) / 100 : null;

      const collectedStyle = moneyCollected != null && moneyCollected <= 0 ? { color: "red" } : {};
      const localMachineProfitPercentageId = profit.Id ?? null;

      return {
        ...machine,
        LastVisitMachineId: original?.LastVisitMachineId ?? machine?.LastVisitMachineId ?? null,
        LocalMachineId: original?.LocalMachineId || keyId,
        MachineTypeId: machine.MachineType?.Id ?? machine?.MachineTypeId ?? null,
        FormulaId: null,

        // Créditos no aplican en este modo
        lastCreditsIn: null,
        lastCreditsOut: null,
        actualCreditsIn: null,
        actualCreditsOut: null,
        netCreditsIn: null,
        netCreditsOut: null,
        creditsCollected: null,

        // Dinero
        lastMoneyIn: lastInMoney,
        lastMoneyOut: lastOutMoney,
        moneyIn: actualMoneyIn,
        moneyOut: actualMoneyOut,
        netMoneyIn,
        netMoneyOut,

        moneyCollected,
        clientAmount,
        ownerAmount,
        collectedStyle,
        note: inputs?.Note || "",
        LocalMachineProfitPercentageId: localMachineProfitPercentageId,
        HasReset: hasReset,
        PostResetStartIn: postResetStartIn,
        PostResetStartOut: postResetStartOut,
      };
    }

    // ============================
    // === MODO CON FORMULA =======
    // ============================
    let actualIn = null;
    let actualOut = null;
    let netIn = null;
    let netOut = null;

    if (isAccumulative) {
      // Mantener comportamiento actual: usuario ingresa acumulados.
      const hasIn =
        inputs?.CreditsIn !== undefined && inputs?.CreditsIn !== null && inputs?.CreditsIn !== "";
      const hasOut =
        inputs?.CreditsOut !== undefined && inputs?.CreditsOut !== null && inputs?.CreditsOut !== "";

      actualIn = hasIn ? Number(inputs.CreditsIn) : null;
      actualOut = hasOut ? Number(inputs.CreditsOut) : null;

      netIn = actualIn != null ? actualIn - lastInCredits : null;
      netOut = actualOut != null ? actualOut - lastOutCredits : null;
    } else {
      // Regla nueva: usuario ingresa NETOS y persistimos acumulados calculados.
      const hasNetIn =
        inputs?.NetCreditsIn !== undefined && inputs?.NetCreditsIn !== null && inputs?.NetCreditsIn !== "";
      const hasNetOut =
        inputs?.NetCreditsOut !== undefined && inputs?.NetCreditsOut !== null && inputs?.NetCreditsOut !== "";

      netIn = hasNetIn ? Number(inputs.NetCreditsIn) : null;
      netOut = hasNetOut ? Number(inputs.NetCreditsOut) : null;

      const baseIn = (lastHadReset && lastPostResetIn != null) ? lastPostResetIn : (lastInCredits ?? 0);
      const baseOut = (lastHadReset && lastPostResetOut != null) ? lastPostResetOut : (lastOutCredits ?? 0);

      actualIn = netIn != null ? baseIn + netIn : null;
      actualOut = netOut != null ? baseOut + netOut : null;
    }

    const creditsCollected = netIn != null && netOut != null ? netIn - netOut : null;

    // Dinero neto = formula aplicada a los netos de creditos
    const netMoneyIn = netIn != null ? calculateFormula(netIn, formula) : null;
    const netMoneyOut = netOut != null ? calculateFormula(netOut, formula) : null;

    // Dinero acumulado a persistir (siempre)
    const moneyIn = actualIn != null ? calculateFormula(actualIn, formula) : null;
    const moneyOut = actualOut != null ? calculateFormula(actualOut, formula) : null;

    const moneyCollected =
      creditsCollected != null ? calculateFormula(creditsCollected, formula) : null;

    const profit = resolveProfitPercentage(machine, profitPercentages, isEditMode);

    const clientAmount =
      moneyCollected != null ? (moneyCollected * (profit.ClientPercentage || 0)) / 100 : null;

    const ownerAmount =
      moneyCollected != null ? (moneyCollected * (profit.OwnerPercentage || 0)) / 100 : null;

    const collectedStyle = moneyCollected != null && moneyCollected <= 0 ? { color: "red" } : {};
    const localMachineProfitPercentageId = profit.Id ?? null;

    return {
      ...machine,
      LastVisitMachineId: original?.LastVisitMachineId ?? machine?.LastVisitMachineId ?? null,
      LocalMachineId: original?.LocalMachineId || keyId,
      MachineTypeId: machine.MachineType?.Id ?? machine?.MachineTypeId ?? null,
      FormulaId: machine.Formula?.Id ?? machine.FormulaId ?? null,

      // Creditos
      lastCreditsIn: lastInCredits,
      lastCreditsOut: lastOutCredits,
      actualCreditsIn: actualIn,
      actualCreditsOut: actualOut,
      netCreditsIn: netIn,
      netCreditsOut: netOut,
      creditsCollected,

      // Dinero
      moneyIn,
      moneyOut,
      netMoneyIn,
      netMoneyOut,
      moneyCollected,

      clientAmount,
      ownerAmount,
      collectedStyle,
      note: inputs?.Note || "",
      LocalMachineProfitPercentageId: localMachineProfitPercentageId,
      HasReset: hasReset,
      PostResetStartIn: postResetStartIn,
      PostResetStartOut: postResetStartOut,
    };
  }
}
